'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Copy01Icon,
  Link02Icon,
  ComputerIcon,
  HierarchyIcon,
  PlayIcon,
} from '@terra/ui-icons';
import type {
  FeatureRelatedRequestDto,
  WebPageFeatureReturnDto,
} from '@terra/shared-contracts-web-pages';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { Spinner } from '../../../components/spinner';
import { Tooltip } from '../../../components/tooltip';
import { BadgeHttp } from '../../../components/styled/badge-http';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import { toasterApi } from '../../toast/ToastProvider';
import { EnhancedCollapsibleWrapper } from '../../EnhancedCollapsibleWrapper';
import { useFeatureRelatedRequests } from '../hooks/useFeatureRelatedRequests';

export type FeatureRelatedRequestActions = {
  onReplay?: (request: FeatureRelatedRequestDto) => void;
  onOpenEndpoint?: (request: FeatureRelatedRequestDto) => void;
  onOpenWebpage?: (request: FeatureRelatedRequestDto) => void;
};
type FeatureRelatedRequestsSectionProps = {
  feature: WebPageFeatureReturnDto;
  webpageId: string;
  actions?: FeatureRelatedRequestActions;
};
const RelatedRequestRow = ({
  request,
  currentWebpageId,
  actions,
}: {
  request: FeatureRelatedRequestDto;
  currentWebpageId: string;
  actions?: FeatureRelatedRequestActions;
}) => {
  const t = useTranslations('designSystem.applicationAssets.drawer.features');
  const tCommon = useTranslations('designSystem.common');
  const copyUrlLabel = t('copyUrl');
  const openEndpointLabel = t('openEndpoint');
  const openWebpageLabel = t('openWebpage');
  const replayLabel = tCommon('replay');
  const canOpenEndpoint =
    Boolean(request.endpointId) && !!actions?.onOpenEndpoint;
  const canOpenWebpage =
    !canOpenEndpoint &&
    Boolean(request.webpageId) &&
    request.webpageId !== currentWebpageId &&
    !!actions?.onOpenWebpage;
  const canReplay = !!actions?.onReplay;
  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(request.url);
      toasterApi.success({ title: t('urlCopied') });
    } catch {
      toasterApi.error({ title: t('urlCopyFailed') });
    }
  }, [request.url, t]);

  return (
    <HStack
      gap={2}
      alignItems="center"
      p={2}
      bg="bg.subtle"
      borderWidth={1}
      borderColor="border.subtle"
      borderRadius="md"
      minW={0}
    >
      <BadgeHttp method={request.method} size="sm" flexShrink={0}>
        {request.method.toUpperCase()}
      </BadgeHttp>
      <Tooltip title={request.url}>
        <MonoSmall color="fg.default" truncate flex={1} minW={0}>
          {request.url}
        </MonoSmall>
      </Tooltip>
      {request.status !== null && (
        <MetaDefault color="fg.muted" flexShrink={0}>
          {request.status}
        </MetaDefault>
      )}
      <HStack gap={1} alignItems="center" flexShrink={0}>
        <Tooltip title={copyUrlLabel}>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={copyUrlLabel}
            onClick={() => void handleCopyUrl()}
          >
            <Copy01Icon size={12} />
          </IconButton>
        </Tooltip>
        {canReplay && (
          <Tooltip title={replayLabel}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={replayLabel}
              onClick={() => actions?.onReplay?.(request)}
            >
              <PlayIcon size={12} />
            </IconButton>
          </Tooltip>
        )}
        {canOpenEndpoint && (
          <Tooltip title={openEndpointLabel}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={openEndpointLabel}
              onClick={() => actions?.onOpenEndpoint?.(request)}
            >
              <Link02Icon size={12} />
            </IconButton>
          </Tooltip>
        )}
        {canOpenWebpage && (
          <Tooltip title={openWebpageLabel}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={openWebpageLabel}
              onClick={() => actions?.onOpenWebpage?.(request)}
            >
              <ComputerIcon size={12} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    </HStack>
  );
};

export const FeatureRelatedRequestsSection = ({
  feature,
  webpageId,
  actions,
}: FeatureRelatedRequestsSectionProps) => {
  const t = useTranslations('designSystem.applicationAssets.drawer.features');
  const [isOpen, setIsOpen] = useState(true);
  const {
    relatedRequests,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useFeatureRelatedRequests({
    webpageId,
    featureId: feature.id,
    enabled: isOpen && feature.harEntryIds.length > 0,
  });

  if (feature.harEntryIds.length === 0) {
    return null;
  }

  return (
    <EnhancedCollapsibleWrapper
      open={isOpen}
      onOpenChange={setIsOpen}
      triggerIcon={<HierarchyIcon size={14} />}
      triggerContent={t('relatedRequests', {
        count: feature.harEntryIds.length,
      })}
    >
      <VStack gap={2} alignItems="stretch">
        {isLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <Spinner size="sm" />
          </Box>
        )}
        {error && (
          <MetaDefault color="fg.error">
            {t('relatedRequestsError')}
          </MetaDefault>
        )}
        {relatedRequests.map((request) => (
          <RelatedRequestRow
            key={request.id}
            request={request}
            currentWebpageId={webpageId}
            actions={actions}
          />
        ))}
        {hasNextPage && (
          <Button
            size="sm"
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {t('loadMore')}
          </Button>
        )}
      </VStack>
    </EnhancedCollapsibleWrapper>
  );
};
